import { formatDate } from '@angular/common';
import { ContactTag } from 'src/app/shared/models/contacts/tag.model';
import { ContactSurvey } from '../../models/contacts/contact-survey.model';
import { ContactUnitType } from '../../models/contacts/contact-unit-types';
import { ContactMethod } from '../../models/enums/contacts/contact-method.enum';
import { GiftingTier } from '../../models/enums/contacts/gifting-tier.enum';
import { ContactAssignment } from '../../models/contacts/contact-assignment.model';
import { ContactModule } from '../../models/enums/contacts/contact-module.enum';

export class ContactsFormatter {
    static readonly formatDepartmentName = formatDepartmentName;
    static readonly formatTagName = formatTagName;
    static readonly formatPreferredContactMethod = formatPreferredContactMethod;
    static readonly formatNpsSurveyDetails = formatNpsSurveyDetails;
    static readonly formatContactGiftingTier = formatContactGiftingTier;
    static readonly formatContactStatus = formatContactStatus;
    static readonly formatUpdatedDate = formatUpdatedDate;
    static readonly getPhoneNumberParts = getPhoneNumberParts;
    static readonly formatPhone = formatPhone;
    static readonly formatUnitDescriptions = formatUnitDescriptions;
}

export function formatDepartmentName(item: ContactUnitType[] | null | undefined): string {
    return item?.map((d) => d.unitTypeName).join(', ') ?? '';
}

export function formatTagName(item: ContactTag[] | null | undefined): string {
    return item?.map((d) => d.tagName).join(', ') ?? '';
}

export function formatPreferredContactMethod(item: ContactMethod | null | undefined): string {
    switch (item) {
        case ContactMethod.Email:
            return 'Email';
        case ContactMethod.Phone:
            return 'Phone';
        case ContactMethod.Text:
            return 'Text';
        case ContactMethod.Teams:
            return 'Teams';
        default:
            return '';
    }
}

export function formatNpsSurveyDetails(item: ContactSurvey | null | undefined): string {
    if (!item) {
        return '';
    }

    const quarters = [];

    if (item.sendFirstQuarter) {
        quarters.push('Q1');
    }
    if (item.sendSecondQuarter) {
        quarters.push('Q2');
    }
    if (item.sendThirdQuarter) {
        quarters.push('Q3');
    }
    if (item.sendFourthQuarter) {
        quarters.push('Q4');
    }

    return quarters.join(', ');
}

export function formatContactGiftingTier(item: GiftingTier | null | undefined): string {
    switch (item) {
        case GiftingTier.Low:
            return 'Low';
        case GiftingTier.Medium:
            return 'Medium';
        case GiftingTier.High:
            return 'High';
        default:
            return '';
    }
}

export function formatContactStatus(val: boolean): string {
    return val ? 'Active' : 'Inactive';
}

export function formatUpdatedDate(value: Date): string {
    return value ? formatDate(value, 'MM/dd/yyyy', 'en-US') : '';
}

export function getPhoneNumberParts(value: string | null | undefined): {
    countryCode: string | number;
    areaCode: string;
    phoneNumber: string;
    phoneExtension: string;
    rawPhone: string;
} {
    let countryCode: string | number = '';
    let areaCode = '';
    let phoneNumber = '';
    let phoneExtension = '';
    let phoneLength = 0;

    const phoneSections = value.split(/ext/gi);
    const phone = phoneSections[0].replace(/\D/g, '');
    phoneLength = phone.length;

    if (phoneSections.length > 1) {
        phoneExtension = phoneSections[1].replace(/\D/g, '');
    }

    switch (phoneLength) {
        case 10: // PPP####### -> 1 (PPP) ###-####
            countryCode = 1;
            areaCode = phone.slice(0, 3);
            phoneNumber = phone.slice(3);
            break;

        case 11: // +CPPP####### -> C (PPP) ###-####
            countryCode = phone[0];
            areaCode = phone.slice(1, 4);
            phoneNumber = phone.slice(4);
            break;

        case 12: // +CCCPP####### -> CCC (PP) ###-####
            countryCode = phone.slice(0, 3);
            areaCode = phone.slice(3, 5);
            phoneNumber = phone.slice(5);
            break;
    }

    return { countryCode, areaCode, phoneNumber, phoneExtension, rawPhone: phone };
}

export function formatPhone(value: string | null | undefined): string {
    if (!value) {
        return '';
    }

    const parts = getPhoneNumberParts(value);
    let { countryCode, phoneExtension } = parts;
    const { areaCode, phoneNumber, rawPhone } = parts;

    if (phoneExtension) {
        phoneExtension = `Ext.${phoneExtension}`;
    }

    if (rawPhone.length < 10 || rawPhone.length > 12) {
        return `${rawPhone} ${phoneExtension}`.trim();
    }

    if (countryCode === 1) {
        countryCode = '';
    }

    return `${countryCode} (${areaCode}) ${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)} ${phoneExtension}`.trim();
}

export function formatUnitDescriptions(
    assignments: readonly ContactAssignment[] | null | undefined,
    unitTitleByKey: ReadonlyMap<string, string> | null | undefined
): string[] {
    if (!assignments?.length || !unitTitleByKey?.size) {
        return [];
    }

    return assignments
        .filter((a) => a.contactType === ContactModule.Unit)
        .map((a) => unitTitleByKey.get(a.entityKey))
        .filter((name): name is string => !!name);
}
